// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useContext } from 'react';
import styled from '@emotion/styled';

import { useDashboardFormListPage } from './hooks';
import FormWidgetForm from 'presentations/views/components/organisms/FormWidgetForm';
import { blueGrayPale } from 'presentations/views/styles/color';
import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Pagination from 'presentations/views/components/atoms/Pagination';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import DashboardHomeNavigation from 'presentations/views/components/organisms/DashboardHomeNavigation';
import DashboardProjectNavigation from 'presentations/views/components/organisms/DashboardProjectNavigation';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import Button from 'presentations/views/components/atoms/Button';
import { Role as TeamUserRole } from 'domains/models/team_user/role';
import PlanUpgradeDialog from 'presentations/views/components/organisms/PlanUpgradeDialog';
import FormListItemComp, {
  LoadingComponent,
} from 'presentations/views/components/molecules/FormListItem';
import { FormListItem } from 'presentations/api/schema/form';
import { Role } from 'domains/models/form_user/role';
import { Status } from 'domains/models/form/status';
import FloatSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';
import FormShareMenu from 'presentations/views/components/molecules/FormShareMenu';
import { formWidgetPath } from 'presentations/routes/path';
import { useRouter } from 'next/router';
import Skeleton from '../../atoms/Skeleton';
import { currentTimeZone } from 'infras/datetime';

interface Props {
  viewer?: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;
  const router = useRouter();

  const { state, actions } = useDashboardFormListPage({
    viewer: viewer,
  });

  let userId: string | null = state.currentUserId || viewer.uid;
  if (userId === viewer.uid) userId = null;

  const renderMenu = (form: FormListItem, role: Role) => {
    if (!state.anchorForm) return null;
    if (state.anchorForm.id !== form.id) return null;
    if (role > Role.Editor) return null;

    const isEnabled = form.status === Status.Enable;

    const options = [
      {
        icon: 'edit',
        label: t('Edit'),
      },
      {
        icon: 'file_copy_outlined',
        label: t('Duplicate'),
      },
      {
        icon: 'delete',
        label: t('Delete'),
      },
      {
        icon: '',
        label: '-',
        divider: true,
      },
      {
        icon: isEnabled ? 'toggle-switch' : 'toggle-switch-off',
        label: t(isEnabled ? 'Deactivation' : 'Activation'),
      },
    ];
    return (
      <FloatSettingMenu
        onCloseMenu={actions.onCloseMenu}
        onSelectMenu={actions.onSelectMenu}
        anchor={state.anchor}
        options={options}
      />
    );
  };

  return (
    <DashboardPageTemplate
      title={t('title')}
      viewer={viewer}
      tabBarContent={
        <Container>
          {state.projectUid ? (
            <DashboardProjectNavigation
              activeIndex={2}
              viewer={viewer}
              projectId={state.projectUid}
              onChangeFilter={actions.onChangeFilter}
            />
          ) : (
            <DashboardHomeNavigation
              activeIndex={2}
              userId={state.currentUserId || viewer.uid}
              viewer={viewer}
              onChangeFilter={actions.onChangeFilter}
            />
          )}
          <ListContainer>
            <ActionContainer>
              {state.currentUserId === 'all' || state.projectUid ? (
                <TitleContainer>
                  <Title>{t('title')}</Title>
                </TitleContainer>
              ) : (
                <TitleContainer>
                  <Title>
                    {state.currentTeamUser ? (
                      <>
                        {viewer.uid === state.currentTeamUser.userId
                          ? t('title')
                          : t('Form Other', {
                              name: state.currentTeamUser.name,
                            })}
                      </>
                    ) : (
                      <Skeleton width="300px" />
                    )}
                  </Title>
                </TitleContainer>
              )}
              {state.currentTeamUser &&
                (!!state.projectUid ||
                  state.currentTeamUser.userId === viewer.uid) && (
                  <ButtonWrap
                    variant="text"
                    size="large"
                    color="primary"
                    onClick={actions.onClickNewForm}
                  >
                    <ButtonIcon name="plus" />
                    <ButtonText>{t('Create')}</ButtonText>
                  </ButtonWrap>
                )}
            </ActionContainer>
            <Content>
              {!state.isLoading && state.forms.length === 0 && (
                <NoContentContainer>
                  <NoContentMain>
                    <NoContentTitle>{t('No Content title')}</NoContentTitle>
                    <NoContentDescription>
                      {t('No Content description')}
                    </NoContentDescription>
                    <NoContentButton
                      color="primary"
                      size="large"
                      onClick={actions.onClickNewForm}
                    >
                      {t('Create Form')}
                    </NoContentButton>
                  </NoContentMain>
                  <NoContentSub>
                    <NoContentImage
                      src="pages/dashboard/form/flow.png"
                      width={440}
                      dpr={2}
                      height={300}
                      alt={t('title')}
                      locale={router.locale}
                    />
                  </NoContentSub>
                </NoContentContainer>
              )}
              {state.isLoading ? (
                <>
                  <FormListItemContainer>
                    <LoadingComponent />
                  </FormListItemContainer>
                  <FormListItemContainer>
                    <LoadingComponent />
                  </FormListItemContainer>
                </>
              ) : (
                state.forms.map((f: FormListItem) => {
                  let role;
                  if (viewer.role <= TeamUserRole.Admin) {
                    role = Role.Admin;
                  } else {
                    const v = f.users.find((u) => u.uid === viewer.uid);
                    role = v ? v.role : Role.Viewer;
                  }
                  return (
                    <FormListItemContainer key={f.uid}>
                      <FormListItemComp
                        teamUid={viewer.team!.uid}
                        teamSlug={viewer.team!.slug}
                        isLoading={state.isLoading}
                        form={f}
                        onClickMenu={actions.onClickMenu}
                        onClickShareMenu={actions.onClickShareMenu}
                        role={role}
                        onClickActivation={() => actions.onSwitchActivation(f)}
                        userTimeZone={
                          state.userProfile?.timeZone || currentTimeZone()
                        }
                      />
                      {renderMenu(f, role)}
                    </FormListItemContainer>
                  );
                })
              )}
              {state.numberOfPages > 1 && (
                <PaginationContainer>
                  <Pagination
                    currentPage={state.currentPage}
                    numberOfPages={state.numberOfPages}
                    onChange={actions.onChangePage}
                  />
                </PaginationContainer>
              )}
            </Content>
          </ListContainer>
          <ConfirmationDialog
            isLoading={state.isConfirmLoading}
            isOpen={state.deleteForm !== null}
            onSubmit={actions.onDeleteForm}
            onCancel={actions.onCloseDialog}
            title={t('Delete?', { name: state.deleteForm?.name })}
            submitLabel={t('Delete')}
          />
          <ConfirmationDialog
            isLoading={state.isConfirmLoading}
            isOpen={state.copyForm !== null}
            onSubmit={actions.onCopyForm}
            onCancel={actions.onCloseDialog}
            title={t('Duplicate?', { name: state.copyForm?.name }, true)}
          />
          {state.embedForm && (
            <FormWidgetForm
              listViewType={state.embedForm.listViewType}
              embedUrl={formWidgetPath(state.embedForm.uid)}
              onClose={actions.onCloseAddToWebsite}
            />
          )}
          {state.isShowingPlanUpgrade && (
            <PlanUpgradeDialog onClose={actions.onClosePlanUpgrade} />
          )}
          <FormShareMenu
            onCloseMenu={actions.onCloseShareMenu}
            onSelectMenu={actions.onSelectShareMenu}
            anchor={state.anchorShare}
            isMobileApp={state.isiOSApp || state.isAndroid()}
          />
        </Container>
      }
    />
  );
}

const Container = styled.div`
  background: ${blueGrayPale};
`;

const Content = styled.div`
  background: ${blueGrayPale};
  padding: 0 ${spacing(24)};
  ${mediaMax.tablet} {
    width: 100%;
    padding: 0 ${spacing(12)};
  }
`;
const PaginationContainer = styled.div`
  padding: ${spacing(48)} 0;
  display: flex;
  justify-content: center;
`;

const ButtonWrap = styled(Button)`
  min-height: auto;
  ${mediaMax.tablet} {
  }
`;
const ButtonIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  /* font-size: ${fontSize(16)} !important; */
  width: 16px !important;
  height: 16px !important;

  ${mediaMax.tablet} {
    width: 24px !important;
    height: 24px !important;
  }
`;

const ButtonText = styled.span`
  ${mediaMax.smartphone} {
    display: none;
  }
`;
const Title = styled.div`
  font-size: ${fontSize(20)};
  font-weight: bold;

  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const TitleContainer = styled.div``;
const ActionContainer = styled.div`
  margin: 0 auto;
  padding: 0 ${spacing(24)} ${spacing(24)};
  display: flex;
  justify-content: space-between;
  ${mediaMax.tablet} {
    padding: 0 0 ${spacing(12)} ${spacing(12)};
    align-items: center;
  }
`;

const CopyMenuItem = styled.div``;
const MenuItemName = styled.div`
  margin-left: ${spacing(12)};
`;

const MenuIcon = styled(Icon)`
  font-size: ${fontSize(20)};
`;
const MenuItem = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
`;
const FormListItemContainer = styled.div`
  margin-bottom: ${spacing(20)};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const ListContainer = styled.div`
  margin-top: ${spacing(24)};

  ${mediaMax.tablet} {
    margin-top: ${spacing(12)};
  }
`;
const NoContentContainer = styled.div`
  display: flex;
  ${mediaMax.tablet} {
    flex-direction: column-reverse;
  }
`;
const NoContentMain = styled.div`
  flex: 1;
`;
const NoContentTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
`;
const NoContentDescription = styled.p`
  margin-bottom: ${spacing(16)};
`;
const NoContentSub = styled.div`
  margin-left: ${spacing(16)};
  ${mediaMax.tablet} {
    margin-left: 0;
    margin-bottom: ${spacing(16)};
  }
`;
const NoContentImage = styled(Image)`
  ${mediaMax.tablet} {
    width: 100%;
    height: auto;
  }
`;
const NoContentButton = styled(Button)``;
